import { Component } from '@theme/component';
import { Shortlist, ShortlistChangeEvent } from '@theme/shortlist';

/**
 * The price, quantity and save row at the base of an item's detail.
 *
 * Two jobs: keep the estimated total in step with the quantity, and hand the quantity to the save
 * control so it travels with the shortlist into the enquiry ("Weekend Bag × 100").
 *
 * **A quantity is stored only once the visitor changes it.** The figure the stepper starts on is
 * the minimum order, which is a fact about the product rather than an answer, and the shortlist's
 * schema (v3) is explicit that a default must never pass for one. Left untouched, the enquiry's
 * quantities step still suggests the headcount, as before.
 *
 * Holds no state of its own beyond that: if the item is already saved with a quantity, the stepper
 * shows the stored one, and a change made in the enquiry flow is picked up through the change event.
 *
 * Cloned out of a `<template>` into the inline panel, so this module is loaded by
 * `curated-lightbox.liquid` and `curated-product.liquid`, not from inside the detail markup.
 */

/**
 * @typedef {Object} QuoteRefs
 * @property {HTMLInputElement} input
 * @property {HTMLElement} [total]
 * @property {HTMLElement} [hint]
 */

/**
 * @extends {Component<QuoteRefs>}
 */
class CuratedQuote extends Component {
  requiredRefs = ['input'];

  /** @type {((event: Event) => void) | null} */
  #onShortlistChange = null;

  /** @type {Intl.NumberFormat | null} */
  #formatter = null;

  connectedCallback() {
    super.connectedCallback();

    try {
      this.#formatter = new Intl.NumberFormat(this.dataset.locale || undefined, {
        style: 'currency',
        currency: this.dataset.currency || 'GBP',
      });
    } catch {
      this.#formatter = null;
    }

    this.#fromStore();
    this.#render();

    this.#onShortlistChange = () => {
      this.#fromStore();
      this.#render();
    };
    document.addEventListener(ShortlistChangeEvent.eventName, this.#onShortlistChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#onShortlistChange) {
      document.removeEventListener(ShortlistChangeEvent.eventName, this.#onShortlistChange);
      this.#onShortlistChange = null;
    }
  }

  /** @returns {void} */
  increase() {
    const step = this.#step();
    const current = this.#quantity();
    // Snap to the step grid on the way up, so 105 goes to 110 rather than 115.
    this.#set(Math.floor(current / step) * step + step);
  }

  /** @returns {void} */
  decrease() {
    const step = this.#step();
    const current = this.#quantity();
    const snapped = Math.ceil(current / step) * step - step;
    this.#set(Math.max(1, snapped));
  }

  /** @returns {void} */
  change() {
    this.#commit();
    this.#render();
  }

  /**
   * @param {number} quantity
   * @returns {void}
   */
  #set(quantity) {
    this.refs.input.value = String(quantity);
    this.#commit();
    this.#render();
  }

  /**
   * The visitor has given a quantity: it now goes with the save, and onto the saved item if there
   * is one.
   *
   * @returns {void}
   */
  #commit() {
    const button = this.#button();
    const variantId = button?.dataset.variantId;
    if (!button || !variantId) return;

    const quantity = this.#quantity();
    button.dataset.quantity = String(quantity);

    if (Shortlist.has(variantId)) Shortlist.setQuantities({ [variantId]: quantity });
  }

  /**
   * Shows the stored quantity when the item is already saved with one.
   *
   * @returns {void}
   */
  #fromStore() {
    const variantId = this.#button()?.dataset.variantId;
    if (!variantId) return;

    const saved = Shortlist.items().find((item) => item.variantId === variantId);
    if (!saved?.quantity) return;
    if (document.activeElement === this.refs.input) return;

    this.refs.input.value = String(saved.quantity);
  }

  /** @returns {void} */
  #render() {
    const quantity = this.#quantity();
    const unitPrice = Number(this.dataset.unitPrice) || 0;

    if (this.refs.total) {
      const total = this.#money((unitPrice * quantity) / 100);
      this.refs.total.textContent = (this.dataset.totalTemplate ?? '')
        .replace('[total]', total)
        .replace('[count]', quantity.toLocaleString(this.dataset.locale || undefined));
    }

    if (this.refs.hint) {
      const moq = Number(this.dataset.moq) || 0;
      this.refs.hint.hidden = !(moq > 1 && quantity < moq);
    }
  }

  /**
   * @param {number} amount - In major units
   * @returns {string}
   */
  #money(amount) {
    return this.#formatter ? this.#formatter.format(amount) : amount.toFixed(2);
  }

  /** @returns {number} A whole number, at least 1 */
  #quantity() {
    const value = Math.floor(Number(this.refs.input.value));
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  /** @returns {number} */
  #step() {
    const step = Number(this.dataset.step);
    return Number.isInteger(step) && step > 0 ? step : 1;
  }

  /** @returns {HTMLElement | null} */
  #button() {
    return this.querySelector('shortlist-button');
  }
}

if (!customElements.get('curated-quote')) {
  customElements.define('curated-quote', CuratedQuote);
}
